import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { Field, inputCls, btnPrimary, btnGhost } from "./ui";

const EMPTY = {
  id: null, title: "", subtitle: "", teaser: "", show: "The Program", slug: "", episode_number: "", publication_state: "draft",
  release_at: "", duration: "", youtube_id: "", youtube_url: "", spotify_url: "", description: "", thumbnail_url: "", thumbnail_alt: "",
  topics: "", chapters: "", sources: "", recipe_id: "", shis_take: "", final_word: "",
};

const epToForm = (e) => ({
  id: e.id,
  title: e.title || "",
  slug: e.slug || "",
  episode_number: e.episode_number ?? "",
  publication_state: e.publication_state || (e.published ? "published" : "draft"),
  release_at: (e.published_at || e.release_at || "").slice(0, 16),
  subtitle: e.subtitle || "",
  teaser: e.teaser || "",
  show: e.show || "The Program",
  duration: e.duration || "",
  youtube_id: e.youtube_id || "",
  youtube_url: e.youtube_url || "",
  spotify_url: e.spotify_url || "",
  description: e.description || "",
  thumbnail_url: e.thumbnail_url || "",
  thumbnail_alt: e.thumbnail_alt || "",
  topics: (e.topics || []).join(", "),
  chapters: (e.chapters || []).map((c) => `${c.time} | ${c.label}`).join("\n"),
  sources: (e.sources || []).map((s) => `${s.label} | ${s.url}`).join("\n"),
  recipe_id: e.recipe_id || "",
  shis_take: e.shis_take || "",
  final_word: e.final_word || "",
});

const formToPayload = (f) => ({
  title: f.title,
  subtitle: f.subtitle || null,
  teaser: f.teaser || null,
  show: f.show,
  slug: f.slug || null,
  episode_number: f.episode_number === "" ? null : Number(f.episode_number),
  publication_state: f.publication_state,
  release_at: f.release_at || null,
  duration: f.duration || null,
  youtube_id: f.youtube_id || null,
  youtube_url: f.youtube_url || null,
  spotify_url: f.spotify_url || null,
  description: f.description || null,
  thumbnail_url: f.thumbnail_url || null,
  thumbnail_alt: f.thumbnail_alt || null,
  topics: f.topics.split(",").map((t) => t.trim()).filter(Boolean),
  chapters: f.chapters
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [time, ...rest] = l.split("|");
      return { time: (time || "").trim(), label: rest.join("|").trim() };
    })
    .filter((c) => c.time && c.label),
  sources: f.sources
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => { const [label, ...rest] = line.split("|"); return { label: label.trim(), url: rest.join("|").trim() }; })
    .filter((source) => source.label && source.url),
  recipe_id: f.recipe_id || null,
  shis_take: f.shis_take || null,
  final_word: f.final_word || null,
});

export default function EpisodesPanel() {
  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    adminApi.get("/episodes").then((r) => setEpisodes(r.episodes)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the episode a title.");
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (form.id) {
        await adminApi.put(`/episodes/${form.id}`, payload);
        toast.success("Episode updated.");
      } else {
        await adminApi.post("/episodes", payload);
        toast.success("Episode created.");
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ep) => {
    if (!window.confirm(`Delete "${ep.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.del(`/episodes/${ep.id}`);
      toast.success("Episode deleted.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-episodes-panel">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-latte">{episodes.length} episode(s) in the archive</p>
        <button
          data-testid="admin-new-episode-button"
          onClick={() => setForm({ ...EMPTY })}
          className={`${btnPrimary} inline-flex items-center gap-2`}
        >
          <Plus size={16} /> New Episode
        </button>
      </div>

      {form && (
        <div data-testid="admin-episode-form" className="mt-8 rounded-2xl border border-gold/30 bg-cocoa p-6 md:p-10">
          <h3 className="font-display text-2xl font-bold text-cream">
            {form.id ? "Edit Episode" : "New Episode"}
          </h3>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Field label="Title" className="md:col-span-2">
              <input data-testid="episode-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Public episode title" />
            </Field>
            <Field label="Show">
              <select data-testid="episode-form-show" className={inputCls} value={form.show} onChange={(e) => setForm({ ...form, show: e.target.value })}>
                <option>The Program</option><option>Let's Elaborate</option>
              </select>
            </Field>
            <Field label="Episode Number">
              <input data-testid="episode-form-number" type="number" min="1" className={inputCls} value={form.episode_number} onChange={(e) => setForm({ ...form, episode_number: e.target.value })} placeholder="1" />
            </Field>
            <Field label="Release date/time (Indianapolis)">
              <input data-testid="episode-form-date" type="datetime-local" className={inputCls} value={form.release_at} onChange={(e) => setForm({ ...form, release_at: e.target.value })} />
            </Field>
            <Field label="Publication state">
              <select data-testid="episode-form-state" className={inputCls} value={form.publication_state} onChange={(e) => setForm({ ...form, publication_state: e.target.value })}>
                <option value="draft">Draft — private</option><option value="review">Review — private</option><option value="published">Published — public</option>
              </select>
            </Field>
            <Field label="Subtitle / teaser">
              <input data-testid="episode-form-subtitle" className={inputCls} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Optional short line" />
            </Field>
            <Field label="YouTube Video ID">
              <input data-testid="episode-form-youtube" className={inputCls} value={form.youtube_id} onChange={(e) => setForm({ ...form, youtube_id: e.target.value })} placeholder="the part after watch?v=" />
            </Field>
            <Field label="YouTube URL (optional)">
              <input data-testid="episode-form-youtube-url" className={inputCls} value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=…" />
            </Field>
            <Field label="Spotify / podcast URL (optional)">
              <input data-testid="episode-form-spotify-url" className={inputCls} value={form.spotify_url} onChange={(e) => setForm({ ...form, spotify_url: e.target.value })} placeholder="https://open.spotify.com/…" />
            </Field>
            <Field label="Duration">
              <input data-testid="episode-form-duration" className={inputCls} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="48 min" />
            </Field>
            <Field label="Description (blank line between paragraphs)" className="md:col-span-2">
              <textarea data-testid="episode-form-description" rows={5} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Approved external thumbnail URL" className="md:col-span-2">
              <input data-testid="episode-form-thumbnail-url" className={inputCls} value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="https://…" />
            </Field>
            <Field label="Thumbnail alt text" className="md:col-span-2">
              <input data-testid="episode-form-thumbnail-alt" className={inputCls} value={form.thumbnail_alt} onChange={(e) => setForm({ ...form, thumbnail_alt: e.target.value })} placeholder="Describe the approved thumbnail" />
            </Field>
            <Field label="Topics (comma separated)">
              <input data-testid="episode-form-topics" className={inputCls} value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} placeholder="culture, music, relationships" />
            </Field>
            <Field label="Slug (optional — auto from title)">
              <input data-testid="episode-form-slug" className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="episode-1-welcome-to-the-show" />
            </Field>
            <Field label="Chapters — one per line: MM:SS | Label" className="md:col-span-2">
              <textarea data-testid="episode-form-chapters" rows={6} className={`${inputCls} font-mono`} value={form.chapters} onChange={(e) => setForm({ ...form, chapters: e.target.value })} placeholder={"00:00 | Cold open\n02:30 | Shi Sips"} />
            </Field>
            <Field label="Public sources / receipts — one per line: Label | https://…" className="md:col-span-2">
              <textarea data-testid="episode-form-sources" rows={4} className={inputCls} value={form.sources} onChange={(e) => setForm({ ...form, sources: e.target.value })} />
            </Field>
            <Field label="Linked Shi Sip recipe ID (optional)">
              <input data-testid="episode-form-recipe-id" className={inputCls} value={form.recipe_id} onChange={(e) => setForm({ ...form, recipe_id: e.target.value })} />
            </Field>
            <Field label="Shi's Take" className="md:col-span-2">
              <textarea data-testid="episode-form-shis-take" rows={3} className={inputCls} value={form.shis_take} onChange={(e) => setForm({ ...form, shis_take: e.target.value })} />
            </Field>
            <Field label="Final Word" className="md:col-span-2">
              <textarea data-testid="episode-form-final-word" rows={3} className={inputCls} value={form.final_word} onChange={(e) => setForm({ ...form, final_word: e.target.value })} />
            </Field>
          </div>
          <p data-testid="episode-publication-help" className="mt-6 text-sm leading-relaxed text-latte">Only <span className="font-semibold text-gold">Published</span> records appear on public pages. A release date never publishes an episode automatically.</p>
          <div className="mt-8 flex gap-3">
            <button data-testid="episode-form-save-button" onClick={save} disabled={saving} className={btnPrimary}>
              {saving ? "Saving…" : "Save Episode"}
            </button>
            <button data-testid="episode-form-cancel-button" onClick={() => setForm(null)} className={btnGhost}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 divide-y divide-corduroy rounded-2xl border border-corduroy">
        {episodes.length === 0 && (
          <p className="px-6 py-10 text-center font-editorial italic text-lg text-latte">
            Nothing pressed yet. Create Episode One.
          </p>
        )}
        {episodes.map((ep) => (
          <div key={ep.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-5">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-gold">EP.{ep.episode_number ?? "—"}</span>
                <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${ep.publication_state === "published" ? "bg-forest text-cream" : "bg-cocoa text-latte border border-corduroy"}`}>
                  {ep.publication_state || "draft"}
                </span>
              </div>
              <p className="mt-1.5 font-display text-lg font-bold text-cream">{ep.title}</p>
              <p className="text-xs text-latte">/episodes/{ep.slug}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`admin-episode-edit-${ep.slug}`} onClick={() => setForm(epToForm(ep))} className="flex h-10 w-10 items-center justify-center rounded-full border border-corduroy text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Edit episode">
                <Pencil size={15} />
              </button>
              <button data-testid={`admin-episode-delete-${ep.slug}`} onClick={() => remove(ep)} className="flex h-10 w-10 items-center justify-center rounded-full border border-corduroy text-cream transition-colors hover:border-wine hover:bg-wine" aria-label="Delete episode">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
